import { useCallback, useEffect, useRef, useState } from 'react';
import { useJobStore } from '@core/job/useJobStore';
import { useJobApplicationStore } from '@core/jobApplication/useJobApplicationStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { useDaySequenceStore } from '@core/daySequence/useDaySequenceStore';
import { Job, JobStatus } from '@core/generated/gql-types';
import useAvailableWorkers from '@/hooks/useAvailableWorkers';
import type { JobDetailView } from '@/modules/jobs/views/jobDetail.view';

export const useJobDetailData = (
  view: JobDetailView,
  jobId: string | undefined,
) => {
  const { fetchJobById } = useJobStore();
  const { fetchWorkerById, getWorkerById } = useWorkerStore();
  const { fetchJobApplicationsForJob } = useJobApplicationStore();
  const { fetchAllDaySequencesByJobId } = useDaySequenceStore();

  const [job, setJob] = useState<Job | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);

  const shouldFetchJob = view.query.job.fetch;
  const shouldFetchApplications = view.query.applications.fetch;
  const shouldFetchAssignedWorker = view.query.assignedWorker.fetch;
  const shouldFetchApplicationWorkers = view.query.applicationWorkers.fetch;
  const shouldFetchDaySequencesOnPageLoad =
    view.query.daySequences.fetchWhen === 'pageLoad';
  const shouldFetchAvailableWorkers =
    view.query.availableWorkers.fetch &&
    job?.itemStatus !== JobStatus.Cancelled;

  const { availableWorkers } = useAvailableWorkers({
    job: shouldFetchAvailableWorkers ? job : null,
  });

  useEffect(() => {
    hasFetchedRef.current = false;
    setIsLoading(true);
  }, [jobId]);

  const fetchJobAndRelatedData = useCallback(async () => {
    if (!jobId) {
      setError('Job ID is required');
      setIsLoading(false);
      return;
    }

    if (!shouldFetchJob) {
      setIsLoading(false);
      return;
    }

    const shouldShowLoading = !hasFetchedRef.current;

    try {
      if (shouldShowLoading) {
        setIsLoading(true);
      }
      const fetchedJob = await fetchJobById(jobId);

      if (!fetchedJob) {
        setError('Job not found');
        setJob(null);
        return;
      }

      setJob(fetchedJob);
      setError(null);

      // Fetch job applications once for this job page load.
      const jobApplications =
        shouldFetchApplications || shouldFetchApplicationWorkers
          ? await fetchJobApplicationsForJob(jobId)
          : [];

      if (shouldFetchAssignedWorker && fetchedJob.userId) {
        const existingWorker = getWorkerById(fetchedJob.userId);
        if (!existingWorker) {
          await fetchWorkerById(fetchedJob.userId);
        }
      }

      if (shouldFetchApplicationWorkers) {
        for (const application of jobApplications ?? []) {
          if (!application.userId) continue;
          const existingWorker = getWorkerById(application.userId);
          if (!existingWorker) {
            await fetchWorkerById(application.userId);
          }
        }
      }

      if (shouldFetchDaySequencesOnPageLoad) {
        const daySequenceStore = useDaySequenceStore.getState();
        const daySequences = daySequenceStore.getDaySequenceByJobId(jobId);
        const meta = daySequenceStore.getDaySequencesMeta(jobId);
        if (daySequences.length === 0 && !meta?.fetchedAt) {
          await fetchAllDaySequencesByJobId(jobId);
        }
      }
    } catch (err) {
      setError('Failed to fetch job details');
      console.error('Error fetching job:', err);
    } finally {
      hasFetchedRef.current = true;
      setIsLoading(false);
    }
  }, [
    jobId,
    shouldFetchJob,
    shouldFetchApplications,
    shouldFetchAssignedWorker,
    shouldFetchApplicationWorkers,
    shouldFetchDaySequencesOnPageLoad,
    fetchJobApplicationsForJob,
    fetchWorkerById,
    getWorkerById,
    fetchJobById,
    fetchAllDaySequencesByJobId,
  ]);

  useEffect(() => {
    void fetchJobAndRelatedData();
  }, [fetchJobAndRelatedData]);

  const refreshJob = useCallback(async () => {
    if (!jobId) return null;
    const updatedJob = await fetchJobById(jobId);
    if (updatedJob) {
      setJob(updatedJob);
    }
    return updatedJob;
  }, [jobId, fetchJobById]);

  return {
    job,
    setJob,
    isLoading,
    error,
    availableWorkers,
    refreshJob,
  };
};
